import { lbBrand } from '@fuse/colors';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import FuseUtils from '@fuse/utils';
import Collapse from '@material-ui/core/Collapse';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import IconLB from 'app/main/design-system/components/icons/Icon';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import FuseNavBadge from '../FuseNavBadge';
import FuseNavItem from '../FuseNavItem';

const useStyles = makeStyles(() => ({
  root: {
    padding: 0,
    '&.open': {
      backgroundColor: lbBrand.navOpenBg,
    },
  },
  item: props => ({
    height: 40,
    width: 'calc(100% - 16px)',
    borderRadius: '0 20px 20px 0',
    paddingRight: 12,
    paddingLeft: props.itemPadding > 80 ? 80 : props.itemPadding,
    color: lbBrand.navInactive,
    '&.active > .list-item-text > span': {
      fontWeight: 600,
    },
    '&:hover': {
      color: lbBrand.navHover,
      '& .list-item-icon': {
        color: lbBrand.navHover,
      },
      '& .list-item-icon div': {
        color: lbBrand.navHover,
      },
      '& .arrow-icon': {
        color: lbBrand.navHover,
      },
    },
    '& .list-item-icon': {
      marginRight: 16,
      color: lbBrand.navInactive,
    },
    '& .list-item-icon div': {
      color: lbBrand.navInactive,
    },
    '& .arrow-icon': {
      color: lbBrand.navInactive,
    },
  }),
}));

function needsToBeOpened(location, item) {
  return location && isUrlInChildren(item, location.pathname);
}

function isUrlInChildren(parent, url) {
  if (!parent.children) {
    return false;
  }

  for (let i = 0; i < parent.children.length; i += 1) {
    if (parent.children[i].children) {
      if (isUrlInChildren(parent.children[i], url)) {
        return true;
      }
    }

    if (parent.children[i].url === url || url.includes(parent.children[i].url)) {
      return true;
    }
  }

  return false;
}

function FuseNavVerticalCollapse(props) {
  const userRole = useSelector(({ auth }) => auth.user.role);
  const [open, setOpen] = useState(() => needsToBeOpened(props.location, props.item));
  const { item, nestedLevel } = props;
  const classes = useStyles({
    itemPadding: nestedLevel > 0 ? 40 + nestedLevel * 16 : 24,
  });
  const { t } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation_todo>
  useEffect(() => {
    if (needsToBeOpened(props.location, props.item)) {
      if (!open) {
        setOpen(true);
      }
    }
  }, [props.location, props.item]);

  function handleClick() {
    setOpen(!open);
  }

  const hasPermission = useMemo(() => FuseUtils.hasPermission(item.auth, userRole), [item.auth, userRole]);
  // A collapse can be open to a wider audience than some of its children: hide it when none is left to show.
  const hasVisibleChild = useMemo(
    () => !item.children || item.children.some(child => FuseUtils.hasPermission(child.auth, userRole)),
    [item.children, userRole],
  );

  if (!hasPermission || !hasVisibleChild) {
    return null;
  }

  return (
    <ul className={clsx(classes.root, open && 'open')}>
      <ListItem
        button
        className={clsx(classes.item, 'list-item')}
        onClick={handleClick}
        component={item.url ? NavLinkAdapter : 'li'}
        to={item.url}
        role="button"
      >
        {item.icon &&
          (item.icon.source === 'FontAwesome' ? (
            <div className="list-item-icon text-16 flex-shrink-0">
              <IconLB source={item.icon.source} name={item.icon.name} size={18} />
            </div>
          ) : (
            <Icon color="action" className="list-item-icon text-16 flex-shrink-0">
              {item.icon.name}
            </Icon>
          ))}

        <ListItemText
          className="list-item-text"
          primary={item.translate ? t(`navigation.${item.translate}`) : item.title}
          classes={{ primary: 'text-14' }}
        />

        {/* Hide the parent's total once expanded: children below already show their own counts. */}
        {item.badge && !(open && item.hideBadgeWhenOpen) && <FuseNavBadge className="mx-4" badge={item.badge} />}

        <IconButton
          disableRipple
          className="w-40 h-40 -mx-12 p-0 focus:bg-transparent hover:bg-transparent"
          onClick={ev => ev.preventDefault()}
        >
          <Icon className="text-16 arrow-icon" color="inherit">
            {open ? 'expand_less' : 'expand_more'}
          </Icon>
        </IconButton>
      </ListItem>

      {item.children && (
        <Collapse in={open} className="collapse-children">
          {item.children.map(_item => (
            <FuseNavItem key={_item.id} type={`vertical-${_item.type}`} item={_item} nestedLevel={nestedLevel + 1} />
          ))}
        </Collapse>
      )}
    </ul>
  );
}

FuseNavVerticalCollapse.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.shape({
      name: PropTypes.string,
      source: PropTypes.string,
    }),
    children: PropTypes.array,
    url: PropTypes.string,
    auth: PropTypes.array,
    translate: PropTypes.string,
    badge: PropTypes.object,
  }),
  nestedLevel: PropTypes.number,
  location: PropTypes.object,
};
FuseNavVerticalCollapse.defaultProps = {};

const NavVerticalCollapse = withRouter(React.memo(FuseNavVerticalCollapse));

export default NavVerticalCollapse;
